import Container from '../../../core/components/atoms/Container';
import { useParams } from 'react-router-dom';
import MemoryCard from '../components/card';
import { Typography } from '../../../core/components/molecules/Typography';
import SearchInput from '../components/search-input';
import { useMemo, useState } from 'react';
import ModalCreateFlashcard from '../components/modal-create-flashcard';
import Flashcard from '../components/flashcard';
import useFlashcardByDisciplineUuid from '../hooks/use-flashcard-by-discipline-uuid';
import { Icon } from '../../../core/components/atoms/Icon';
import { Breadcrumbs } from '../../../core/components/organism/Breadcrumbs';
import MemoryButton from '../components/memory-button';
import { flashcardService } from '../queries';
import { toast } from '../../../core/components/atoms/Toast';
import { useUserStore } from '../../Auth/stores/authStore';
import ModalDeleteFlashcards from '../components/modal-delete-flashcards';
import useTag from '../hooks/use-tag';
import BrainLoadingWrapper from '../../../core/components/molecules/BrainLoading';

const DisciplineFlashcardsPage = () => {
  const { user } = useUserStore();
  const { disciplineUuid } = useParams();
  const { flashcards, isLoading, fetchFlashcards } =
    useFlashcardByDisciplineUuid({
      disciplineUuid: disciplineUuid || '',
    });

  const { tag } = useTag(disciplineUuid);
  const slugDiscipline = tag?.slug;

  const userUuid = user?.memoryUserUuid;

  const [openModal, setOpenModal] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [selectedFlashcard, setSelectedFlashcard] = useState<string[]>([]);
  const [editFlashcardUuid, setEditFlashcardUuid] = useState<
    string | undefined
  >(undefined);

  const [isOpenModalDeleteFlashcards, setIsOpenModalDeleteFlashcards] =
    useState(false);

  const [search, setSearch] = useState('');

  const filteredFlashcards = flashcards?.flashcardsDecks?.filter((flashcard) =>
    flashcard.front.toLowerCase().includes(search.toLowerCase())
  );

  const handleEditFlashcard = (flashcardUuid: string) => {
    setEditFlashcardUuid(flashcardUuid);
    setOpenModal(true);
  };

  const handleCancelDeleteMode = () => {
    setIsDeleteMode(false);
    setSelectedFlashcard([]);
    setIsOpenModalDeleteFlashcards(false);
  };

  const handleSelectAllFlashcards = () => {
    if (selectedFlashcard.length === filteredFlashcards?.length) {
      setSelectedFlashcard([]);
    } else {
      setSelectedFlashcard(
        filteredFlashcards?.map((flashcard) => flashcard.uuid) || []
      );
    }
  };

  const handleDeleteSelectedFlashcards = async () => {
    if (selectedFlashcard.length === 0 || !userUuid) return;

    try {
      const response = await flashcardService.deleteMany(
        userUuid,
        selectedFlashcard
      );
      fetchFlashcards({ disciplineUuid: disciplineUuid || '' });
      handleCancelDeleteMode();
      toast({
        title: 'Flashcards deletados com sucesso',
        message: response.message,
        variant: 'success',
      });
    } catch (error) {
      console.log('error', error);
      toast({
        title: 'Erro ao deletar flashcards',
        message: 'Ocorreu um erro ao deletar os flashcards',
        variant: 'error',
      });
    }
  };

  const handleSelectFlashcard = (flashcardUuid: string) => {
    setSelectedFlashcard((prev) => {
      if (prev.includes(flashcardUuid)) {
        return prev.filter((uuid) => uuid !== flashcardUuid);
      }
      return [...prev, flashcardUuid];
    });
  };

  const labelSelectAll = useMemo(() => {
    return selectedFlashcard.length === filteredFlashcards?.length
      ? 'Desmarcar todos'
      : 'Selecionar todos';
  }, [selectedFlashcard, filteredFlashcards]);

  const tagSlug = tag?.slug;

  return (
    <Container className='flex flex-col items-center justify-center'>
      <div className='flex w-full flex-col gap-8 '>
        <Breadcrumbs
          items={[
            { label: 'Flashcards', href: '/memory' },
            { label: flashcards?.name, href: '#' },
          ]}
        />
        <Typography className='text-h5 font-bold text-mono-color-darker'>
          {flashcards?.name}
        </Typography>

        <div className='flex w-full flex-col justify-between gap-2 md:flex-row'>
          <div className='w-full md:w-[171px]'>
            <SearchInput
              search={search}
              setSearch={setSearch}
              placeholder='Procure seus Flashcards...'
            />
          </div>
          <div className='flex flex-row items-center gap-4'>
            <ModalCreateFlashcard
              isDiscipline
              btnCreateLabel={
                <Typography className='text-color-brand-primary'>
                  Criar <i>Flashcard</i>
                </Typography>
              }
              onCallbackSuccess={() => {
                fetchFlashcards({ disciplineUuid: disciplineUuid || '' });
              }}
              open={openModal}
              editFlashcardUuid={editFlashcardUuid}
              onClose={() => {
                setOpenModal(false);
                setEditFlashcardUuid(undefined);
              }}
              defaultDiscipline={slugDiscipline}
            />
            <Icon
              type='material'
              Icon='edit_note'
              className='cursor-pointer text-color-brand-primary'
              onClick={() => setIsDeleteMode(!isDeleteMode)}
            />
          </div>
        </div>
        {isLoading ? (
          <div className='flex h-[174px] w-full items-center justify-center '>
            <BrainLoadingWrapper customMessage='Carregando...' />
          </div>
        ) : (
          <>
            {filteredFlashcards?.length === 0 ? (
              <div className='flex h-[412px]  flex-col items-center justify-center gap-4 rounded-[32px] bg-mono-color-lighter'>
                <div className='flex h-[174px]  items-center justify-center text-center'>
                  <Typography className='p-4 text-xs font-bold !text-mono-color-dark dark:!text-[#2E3847]'>
                    Você ainda não tem <i>flashcards</i> aqui. Crie um novo ou
                    importe de uma disciplina para começar.
                  </Typography>
                </div>
              </div>
            ) : (
              <div className='mb:p-8 grid grid-cols-[repeat(auto-fill,minmax(125px,125px))] justify-center gap-8 rounded-[32px] bg-mono-color-lighter p-2'>
                {filteredFlashcards?.map((flashcard) => (
                  <Flashcard
                    key={flashcard.uuid}
                    title={flashcard.front}
                    onEdit={() => handleEditFlashcard(flashcard.uuid)}
                    isDeleteMode={isDeleteMode}
                    uuid={flashcard.uuid}
                    onSelect={() => {
                      handleSelectFlashcard(flashcard.uuid);
                    }}
                    isSelected={selectedFlashcard.includes(flashcard.uuid)}
                    discipline={tagSlug}
                    subject={flashcard.tags?.[0]?.name}
                    onCallbackSuccess={() => {
                      setEditFlashcardUuid(undefined);
                      fetchFlashcards({ disciplineUuid: disciplineUuid || '' });
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
        {isDeleteMode && (
          <div className='flex w-full flex-col justify-between gap-2 md:flex-row'>
            <div className='flex w-full flex-col gap-2 md:flex-row '>
              <MemoryButton
                label={labelSelectAll}
                variant='outline'
                onClick={handleSelectAllFlashcards}
                className='border-none'
                icon={<Icon type='material' Icon='check' className='text-md' />}
                allowReadOnly
              />
              <MemoryButton
                label='Cancelar'
                variant='outline'
                className='w-full md:w-[150px]'
                onClick={handleCancelDeleteMode}
                allowReadOnly
              />
            </div>
            <div className='flex w-full flex-row gap-2 md:w-auto'>
              <div className='flex w-full flex-row items-center gap-2 md:w-auto'>
                <MemoryButton
                  label='Apagar'
                  variant='primary'
                  className='w-full'
                  onClick={() => setIsOpenModalDeleteFlashcards(true)}
                  disabled={selectedFlashcard.length === 0}
                  icon={
                    <Icon type='material' Icon='delete' className='text-md' />
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <ModalDeleteFlashcards
        isOpen={isOpenModalDeleteFlashcards}
        onClose={handleCancelDeleteMode}
        onDelete={handleDeleteSelectedFlashcards}
      />
    </Container>
  );
};

export default DisciplineFlashcardsPage;
